import type { SapCodeOption } from '@hcc/types';
import type { SapFormField } from './mappingTypes';

/**
 * Per-option translation for SAP code fields: each option value on the
 * mapped form field picks the SAP code it becomes (e.g. "Female" -> 2).
 * Untranslated options surface on the review screen as problems, so
 * blanks here are allowed but visible.
 */
export function ValueMapEditor({
  field,
  codeOptions,
  valueMap,
  onChange,
}: {
  field: SapFormField;
  codeOptions: SapCodeOption[];
  valueMap: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}) {
  if (!field.options || field.options.length === 0) {
    return (
      <p className="text-xs text-fg-dim">
        The mapped field has no options; its raw value must already be a SAP code.
      </p>
    );
  }
  return (
    <div className="space-y-1.5">
      {field.options.map((opt) => (
        <div key={opt.value} className="flex items-center gap-2 text-xs">
          <span className="w-40 truncate text-fg-muted" title={opt.label}>
            {opt.label}
          </span>
          <span className="text-fg-dim">→</span>
          <select
            className="rounded-token-sm border border-line/20 bg-surface px-2 py-1 text-xs"
            value={valueMap[opt.value] ?? ''}
            onChange={(e) => {
              const next = { ...valueMap };
              if (e.target.value === '') delete next[opt.value];
              else next[opt.value] = e.target.value;
              onChange(next);
            }}
          >
            <option value="">Not translated</option>
            {codeOptions.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label} ({c.value})
              </option>
            ))}
          </select>
        </div>
      ))}
    </div>
  );
}
